import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InputNumber, Modal, Select } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { toast } from 'react-toastify';
import * as api from '@apis/payment-statement';
import * as salesChannelApi from '@apis/sales-channels';
import {
  AdminRefundBody,
  PaymentStatementPurchase,
  RefundStatus,
  UpdatePaymentStatementEventType,
} from '@models/paymentStatement';
import useRefundModeModalStore from '@stores/refundModeModalStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import './RefundModeModal.scss';
import moment from 'moment';
import { RefundLog, RefundLogStatus } from '@models/refundLog';
import { usePayment } from '@hooks/payment';
import SlideMenu from '@components/common/SlideMenu/SlideMenu';
import { getPaymentMethodText, combinedRefundItemsByName } from '@utils/payment';
import { getChannelText } from '@utils/string';
import {
  CancellationFeeCalculateType,
  CancelReasonType,
  KakaoReservationStatus,
  RequestCancelReservation,
  SalesChannelsType,
} from '@models/sales-channels';
import { REFUND_REASON_FAULTER } from '@consts/product-order';
import { discountedAmountBy } from '@utils/common';
import { getStatementMembershipDiscountPercent } from '@utils/membership-discount';
import { getPaymentDiscountRows } from '@utils/payment-discount-display';

const SHIPPING_LABELS: { [key: string]: string } = {
  name: '이름',
  phone: '전화번호',
  fee: '배송비',
  address: '기본주소',
  addressDetail: '상세주소',
};

const RefundModeModal: React.FC = () => {
  const { show, close, data } = useRefundModeModalStore();
  const dialog = useOkCancelDialog();
  const payment = usePayment();
  const queryClient = useQueryClient();
  const [noMoreRefundsAvailable, setNoMoreRefundsAvailable] = useState(false);
  const [refundTargets, setRefundTargets] = useState<PaymentStatementPurchase[]>([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [kakaoOrderRefundReason, setKakaoOrderRefundReason] = useState<CancelReasonType>();
  const memoRef = useRef<HTMLTextAreaElement>(null);
  const combinedRefundItems = combinedRefundItemsByName(data?.refunds);

  let remainAmount = (data?.payment.amount || 0) - (data?.refund?.amount || 0);
  const originalRemainAmount =
    remainAmount + (data?.payment.membershipDiscountAmount || 0) - (data?.refund?.membershipDiscountAmount || 0);
  if (data?.channel === SalesChannelsType.NAVER_STORE || data?.channel === SalesChannelsType.EZWEL) {
    if (data?.statuses?.refund === RefundStatus.ALL) {
      remainAmount = 0;
    }
  }
  const remainPoint = (data?.payment.point || 0) - (data?.refund?.point || 0);
  const remainAmountText = `${remainAmount.toLocaleString()}원${
    remainPoint > 0 ? `(${remainPoint.toLocaleString()}P)` : ''
  }`;
  const refundStatusesByUser: string[] = [RefundStatus.ACCEPTED, RefundStatus.DENIED, RefundStatus.REQUESTED];
  const paidPoint = data?.payment?.point || 0;
  const paidAmount = `${data?.payment.amount.toLocaleString()}원${
    paidPoint > 0 ? `(${paidPoint.toLocaleString()}P)` : ''
  }`;
  const reservedItems =
    (data?.statuses.refund === RefundStatus.RESERVED ? data.refund.refundItems || data.refund.purchases : []) || [];
  const discountPercent = getStatementMembershipDiscountPercent(data);
  const membershipReserveRefundAmount =
    reservedItems.length > 0
      ? reservedItems.reduce(
          (prev, cur) => prev + (cur.count || 1) * discountedAmountBy(cur.fee || 0, discountPercent),
          0,
        )
      : 0;
  const membershipRefundAmount =
    (refundTargets.length > 0
      ? refundTargets.reduce(
          (prev, cur) =>
            prev + discountedAmountBy((cur.fee || 0) / (cur.count || 1), discountPercent) * (cur.count || 1),
          0,
        )
      : 0) + membershipReserveRefundAmount;

  const remainCouponDiscountAmount =
    (data?.payment.couponDiscountAmount || 0) - (data?.refund?.couponDiscountAmount || 0);
  const remainCouponDiscountAmountText = `(쿠폰: ${remainCouponDiscountAmount.toLocaleString()}원)`;

  const refundLogsQuery = useQuery({
    queryKey: ['refund-logs', data?._id],
    queryFn: () => api.getRefundLogs(data!._id),
    enabled: !!data?._id,
  });
  const refundPaymentStatementMutation = useMutation({
    mutationFn: api.requestRefundWithBody,
    onSuccess: async (_, body) => {
      let content = '';

      if ('refundItems' in body.data) {
        content = '환불 처리를 성공적으로 완료했습니다.';
      } else {
        content = '"추가 환불 불가" 상태를 변경했습니다.';
      }

      toast.dark(content, {
        pauseOnFocusLoss: false,
        bodyStyle: {
          textAlign: 'center',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-payment-statement'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      close();
    },
    onError: (e: any) => {
      dialog.open({
        title: '환불 실패',
        content: `${e?.response?.data?.message}`,
        type: 'ok',
      });
    },
  });

  const refundNaverOrderMutation = useMutation({
    mutationFn: salesChannelApi.requestRefundNaverOrder,
    onSuccess: async () => {
      toast.dark('환불 처리를 성공적으로 완료했습니다.', {
        pauseOnFocusLoss: false,
        bodyStyle: {
          textAlign: 'center',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-payment-statement'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      close();
    },
    onError: async (e: any) => {
      const orderId = e?.response?.data?.data;
      dialog.open({
        title: '환불 실패',
        content: (
          <div>
            {e?.response?.data?.message}
            {orderId && (
              <Button
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  navigator.clipboard.writeText(orderId);
                  toast.dark(`주문번호를 복사했습니다(${orderId})`, {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    draggable: true,
                    progress: undefined,
                    bodyStyle: {
                      color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                      textAlign: 'center',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '20px',
                    },
                    theme: 'dark',
                  });
                }}>
                복사
              </Button>
            )}
          </div>
        ),
        type: 'ok',
      });
    },
  });

  const changeKakaoReservationStatusMutation = useMutation({
    mutationFn: salesChannelApi.changeKakaoReservationStatus,
    onSuccess: async () => {
      const changeType = changeKakaoReservationStatusMutation?.variables?.type;
      const actionTypeText =
        changeType === 'cancel' ? '환불' : changeType === 'complete' ? '이용완료' : '이용완료 철회';
      toast.dark(`${actionTypeText} 처리가 완료되었습니다.`, {
        pauseOnFocusLoss: false,
        bodyStyle: {
          textAlign: 'center',
        },
      });

      await queryClient.invalidateQueries({ queryKey: ['kakao-reservation'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      close();
    },
    onError: () => {
      const changeType = changeKakaoReservationStatusMutation?.variables?.type;
      const actionTypeText =
        changeType === 'cancel' ? '환불' : changeType === 'complete' ? '이용완료' : '이용완료 철회';
      toast(`${actionTypeText} 처리에 실패했습니다.`, {
        pauseOnFocusLoss: false,
        bodyStyle: {
          textAlign: 'center',
        },
      });
    },
  });

  const refundBenepiaOrderMutation = useMutation({
    mutationFn: salesChannelApi.requestRefundBenepiaOrder,
    onSuccess: async () => {
      toast.dark('환불 처리를 성공적으로 완료했습니다.', {
        pauseOnFocusLoss: false,
        bodyStyle: {
          textAlign: 'center',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-payment-statement'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-search-product-orders'] });
      close();
    },
    onError: async (e: any) => {
      dialog.open({
        title: '환불 실패',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const refundDoneHistory = useMemo(
    () => refundLogsQuery.data?.data?.filter((item) => item?.status === RefundLogStatus.DONE) || [],
    [refundLogsQuery.data, show],
  );

  const USER_REFUND_COST =
    data?.statuses.refund === RefundStatus.ACCEPTED ? data?.refund.acceptedAmount : data?.refund?.requestedAmount;

  const PARTNER_REPLY_STATUS: { [key: string]: string } = {
    denied: '환불 반려',
    accepted: '환불 예정',
    requested: '미응답',
    default: '미응답',
  };
  const REFUND_MEMO = [
    {
      title: '구매자 메모',
      childrens: [
        { key: '요청 사항', value: data?.memo?.payment || '-' },
        { key: '환불 사유', value: data?.memo?.refundUser?.split('/')[0] || '-' },
        { key: '환불 내용', value: data?.memo?.refundUser?.split('/')[1] || '-' },
      ],
    },
    {
      title: '판매자 메모',
      childrens: [
        {
          key: '응답 상태',
          value: PARTNER_REPLY_STATUS[data?.statuses.refund || 'default'] || PARTNER_REPLY_STATUS['default'],
        },
        { key: '메모 내용', value: data?.memo?.refundPartner || '-' },
        {
          key: '승인 상품',
          value:
            data?.statuses.refund === RefundStatus.ACCEPTED
              ? (data.refund.refundItems || data.refund.purchases)
                  ?.map((item) => `- ${item.name}(${((item.fee || 0) * (item.count || 1)).toLocaleString()}원)`)
                  .join('\n')
              : '-',
        },
      ],
    },
  ];
  const PURCHASE_INFO = {
    title: '결제 내역',
    childrens: [
      { key: '구매자', value: data?.buyer.name },
      { key: '주문번호', value: data?.uuid || data?.uid },
      { key: '연락처', value: data?.buyer.phone },
      { key: '결제금액', value: paidAmount },
      ...getPaymentDiscountRows({
        product: data?.product,
        payment: data?.payment,
        purchases: data?.purchases,
      }).map((row) => ({ key: row.label, value: row.value })),
      { key: '결제수단', value: data?.payment ? getPaymentMethodText(data.payment) : '-' },
      { key: '결제시각', value: moment(data?.payment.paidAt).format('YYYY-MM-DD HH:mm') },
      { key: '입력옵션', value: data?.memo?.qna || '-' },
    ],
  };
  const REFUND_COLUMNS: ColumnsType<PaymentStatementPurchase> = [
    {
      title: '선택',
      width: 20,
      render: (item) => {
        const isReserved = reservedItems.length > 0;
        const refundItemIndex = getRefundItemIndex(item);
        const isPossibleAmount =
          (item.count * item.fee || 0) - (getMatchedItem(combinedRefundItems, item)?.fee || 0) > 0;
        let disableCondition = item.fee === 0 ? true : isReserved || !isPossibleAmount;
        if (data?.channel === SalesChannelsType.NAVER_STORE && data?.statuses?.refund === RefundStatus.NONE) {
          disableCondition = false;
        }

        return (
          <Checkbox
            disabled={disableCondition}
            checked={item.fee !== 0 && refundItemIndex !== -1}
            onChange={() => toggleRefundTargets(item)}
          />
        );
      },
      align: 'center',
    },
    {
      title: '상품명',
      width: 200,
      render: (item) => {
        let option = '';
        if (item.option && item.option.length > 0) {
          option = `[${item.option.join(' ')}]`;
        }
        return option + item.name;
      },
      align: 'center',
    },
    {
      title: '개당 가격',
      width: 50,
      render: (item) => `${(item.fee || 0).toLocaleString()}`,
      align: 'center',
    },
    {
      title: '구매 수량',
      width: 30,
      render: (item) => item.count || 0,
      align: 'center',
    },
    {
      title: '남은 수량',
      width: 30,
      render: (item) => item.count - (getMatchedItem(combinedRefundItems, item)?.count || 0),
    },
    {
      title: '요청 수량',
      width: 30,
      render: (item) => {
        if (data?.channel) {
          return item?.count || 0;
        }
        if (data?.statuses.refund && refundStatusesByUser.includes(data.statuses.refund)) {
          return getMatchedItem(data.refund.refundItems || data.refund.purchases, item)?.count || 0;
        } else {
          return 0;
        }
      },
      className: 'red',
    },
    {
      title: '환불 수량',
      width: 30,
      render: (item) => {
        const isReserved = reservedItems.length > 0;
        const refundTarget = getMatchedItem(refundTargets, item);
        const isFullRefundChannel =
          data?.channel === SalesChannelsType.NAVER_STORE || data?.channel === SalesChannelsType.KAKAO_RESERVATION;
        let disableCondition = item.fee === 0 ? true : !refundTarget || isReserved;
        if (isFullRefundChannel) {
          disableCondition = true;
        }

        return (
          <InputNumber
            value={refundTarget?.count || 0}
            min={0}
            max={item.count - (getMatchedItem(combinedRefundItems, item)?.count || 0)}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                const refundItemIndex = getRefundItemIndex(item);
                newValue[refundItemIndex].count = value || 0;
                newValue[refundItemIndex].fee = (value || 0) * item.fee;
                return newValue;
              });
            }}
            disabled={disableCondition}
          />
        );
      },
      align: 'center',
      className: 'red',
    },
    {
      title: '환불 금액',
      width: '140px',
      render: (item) => {
        const isReserved = reservedItems.length > 0;
        const refundTarget = getMatchedItem(refundTargets, item);

        const isFullRefundChannel =
          data?.channel === SalesChannelsType.NAVER_STORE || data?.channel === SalesChannelsType.KAKAO_RESERVATION;
        let disableCondition = item.fee === 0 ? true : !refundTarget || isReserved;
        if (isFullRefundChannel) {
          disableCondition = true;
        }

        return (
          <InputNumber
            value={refundTarget?.fee || 0}
            min={0}
            max={originalRemainAmount}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                const refundItemIndex = getRefundItemIndex(item);
                newValue[refundItemIndex].fee = value || 0;

                return newValue;
              });
            }}
            disabled={disableCondition}
            addonAfter={
              !(!refundTarget || isReserved) && (
                <Select
                  disabled={disableCondition}
                  value={Math.round(((refundTarget.fee || 0) / (refundTarget.count * item.fee)) * 100 || 100)}
                  popupMatchSelectWidth={false}
                  suffixIcon={null}
                  options={Array.from({ length: 20 }, (_, idx) => ({
                    value: 5 * (idx + 1),
                    label: `${5 * (idx + 1)}%`,
                  }))}
                  onChange={(e) => {
                    setRefundTargets((prev) => {
                      const newValue = [...prev];
                      const refundItemIndex = getRefundItemIndex(item);
                      newValue[refundItemIndex].fee = Math.round(
                        (item.fee || 0) * (refundTarget?.count || 0) * (e / 100),
                      );
                      return newValue;
                    });
                  }}
                />
              )
            }
          />
        );
      },
      align: 'center',
    },
  ];
  const REFUND_RESERVE_COLUMNS: ColumnsType<PaymentStatementPurchase> = [
    {
      title: '상품명',
      width: 200,
      render: (item) => {
        let option = '';
        if (item.option && item.option.length > 0) {
          option = `[${item.option.join(' ')}]`;
        }
        return option + item.name;
      },
      align: 'center',
    },
    {
      title: '개당 가격',
      width: 50,
      render: (item) => getMatchedItem(data?.purchases, item)?.fee?.toLocaleString(),
      align: 'center',
    },
    {
      title: '구매 수량',
      width: 30,
      render: (item) => getMatchedItem(data?.purchases, item)?.count,
      align: 'center',
    },
    {
      title: '남은 수량',
      width: 30,
      render: (item) =>
        (getMatchedItem(data?.purchases, item)?.count || 0) - (getMatchedItem(combinedRefundItems, item)?.count || 0),
      align: 'center',
    },
    {
      title: '환불 수량',
      width: 30,
      render: (item) => item.count || 0,
      align: 'center',
    },
    {
      title: '환불 가격',
      width: 30,
      render: (item) =>
        item.count !== 0 ? (item.fee * item.count).toLocaleString() || 0 : item.fee?.toLocaleString() || 0,
      align: 'center',
    },
  ];

  const getMatchedItem = (searchItems?: PaymentStatementPurchase[], target?: PaymentStatementPurchase) => {
    if (searchItems && target) {
      const index = searchItems.findIndex((searchItem) => {
        if (target?.uid) {
          return searchItem?.uid === target?.uid;
        } else {
          return JSON.stringify(searchItem.name) === JSON.stringify(target?.name);
        }
      });
      return searchItems[index];
    }
  };

  const getRefundItemIndex = (target: PaymentStatementPurchase) => {
    return refundTargets.findIndex((refundTarget) => JSON.stringify(refundTarget.name) === JSON.stringify(target.name));
  };
  const toggleRefundTargets = (target: PaymentStatementPurchase) => {
    const refundItemIndex = getRefundItemIndex(target);
    const acceptedAmount = data?.statuses.refund === RefundStatus.ACCEPTED ? data?.refund.acceptedAmount : 0;

    setRefundTargets((prev) => {
      const newValue = [...prev];
      if (refundItemIndex !== -1) {
        newValue.splice(refundItemIndex, 1);
      } else {
        newValue.push({ ...target, count: 0, fee: acceptedAmount || 0 });
      }

      return newValue;
    });
  };
  const checkCanRefund = () => {
    const isCanRefund = remainAmount - membershipRefundAmount >= 0 && refundAmount > 0;
    return isCanRefund;
  };

  const returnRefundItems = (classType?: string) => {
    if (classType === 'commerce' || classType === 'class') {
      const refundItems =
        reservedItems.length > 0
          ? reservedItems
          : refundTargets.map((item) => {
              return {
                ...item,
                fee: Math.floor((item.fee || 0) / (item.count || 1)),
              };
            });
      return refundItems;
    } else {
      throw new Error('상품 클래스 타입이 명확하지 않습니다.');
    }
  };
  const refundSubmitText = (classType?: string) => {
    if (classType === 'class') {
      return `남은 금액 ${(
        (data?.payment.amount || 0) - (data?.refund?.amount || 0)
      ).toLocaleString()}원 중 ${refundAmount?.toLocaleString()}원을 환불하시겠습니까?`;
    } else if (classType === 'commerce') {
      if (refundAmount !== 0) {
        return `유저가 요청한 금액 ${USER_REFUND_COST?.toLocaleString()}원 중 ${refundAmount?.toLocaleString()}원을\n 환불처리 하시겠습니까?`;
      } else {
        return '추가 환불 불가 상태를 변경하시겠습니까?';
      }
    } else {
      throw new Error('상품 클래스 타입이 명확하지 않습니다.');
    }
  };
  const reserveRefundPayment = async () => {
    let totalFee = 0;
    const refundItems: PaymentStatementPurchase[] = refundTargets.map((item) => {
      totalFee += item.fee;

      return {
        ...item,
        fee: Math.floor((item.fee || 0) / (item.count || 1)),
      };
    });
    const canRefund = checkCanRefund();

    if (data && refundTargets.length > 0 && canRefund && memoRef.current) {
      await payment.updateRefundStatus(
        data._id,
        UpdatePaymentStatementEventType.RESERVE,
        refundItems,
        memoRef.current.value,
        noMoreRefundsAvailable,
        totalFee,
      );
      close();
      setRefundTargets([]);

      if (memoRef.current) {
        memoRef.current.value = '';
      }
    } else {
      dialog.open({
        type: 'ok',
        confirmColor: '#FF3D8F',
        title: '실패했습니다.',
        content: !canRefund ? '환불 금액을 확인해주세요!' : '상품을 선택해주세요!',
      });
    }
  };
  const cancelReserveRefundPayment = () => {
    if (data) {
      if (data?.statuses?.refund === RefundStatus.RESERVED) {
        payment.updateRefundStatus(data._id, UpdatePaymentStatementEventType.REMOVE_RESERVE);
        close();
        setRefundTargets([]);

        if (memoRef.current) {
          memoRef.current.value = '';
        }
      }
    }
  };
  const submitRefund = () => {
    if (data?.channel === SalesChannelsType.EZWEL) {
      dialog.open({
        title: '이지웰 환불 안내',
        content: (
          <div>
            이지웰 결제건은 어드민에서 환불할 수 없습니다.
            <br />
            상품주문번호와 상세내용을 이지웰 소통 담당자에게 전달해주세요.
          </div>
        ),
        type: 'ok',
      });
      return;
    }
    const saleType = data?.product?.saleType || 'commerce';
    const refundItems = returnRefundItems(saleType);
    const submitContent = refundSubmitText(saleType);
    const canRefund = checkCanRefund();
    const isKakaoOrder = data?.channel === SalesChannelsType.KAKAO_RESERVATION;
    const isNaverOrder = data?.channel === SalesChannelsType.NAVER_STORE;
    const isBenepiaOrder = data?.channel === SalesChannelsType.BENEPIA;
    if (data?.channel) {
      if (isKakaoOrder) {
        if (!kakaoOrderRefundReason || !data) {
          dialog.open({
            title: '필수',
            content: kakaoOrderRefundReason ? '환불할 수 있는 항목이 없습니다.' : '환불 사유를 고르지 않았습니다.',
            type: 'ok',
          });
          return;
        }
      }

      if (!refundItems?.length || refundAmount < 1) {
        let content = '환불할 옵션과 수량을 선택해주세요';
        if (refundItems?.length !== data?.purchases?.length) {
          content = '전체 목록을 선택해주세요.';
        }
        dialog.open({
          content: `${content}`,
          type: 'ok',
        });
        return;
      }
    }

    dialog.open({
      type: 'ok_cancel',
      title: '환불',
      content: submitContent,
      onConfirm: async () => {
        if (data && memoRef.current && refundItems && canRefund) {
          const refundBodyData = {
            fee: refundAmount || refundTargets.reduce((prev, cur) => prev + (cur.fee || 0), 0),
            refundItems,
            memo: memoRef?.current?.value,
            noMoreRefundsAvailable,
          } as AdminRefundBody;

          if (data?.channel) {
            refundBodyData['noMoreRefundsAvailable'] = true;
            if (isKakaoOrder) {
              submitKakaoOrderRefund();
            } else if (isNaverOrder) {
              refundNaverOrderMutation.mutateAsync({
                productOrderId: data?.uid,
                data: refundBodyData,
              });
              return;
            } else if (isBenepiaOrder) {
              refundBenepiaOrderMutation.mutateAsync({
                productOrderId: data?._id,
                data: refundBodyData,
              });
              return;
            }
          } else {
            await refundPaymentStatementMutation.mutateAsync({
              paymentStatementId: data._id,
              data: refundBodyData,
            });
          }
        } else if (data && refundAmount === 0) {
          await refundPaymentStatementMutation.mutateAsync({
            paymentStatementId: data._id,
            data: { noMoreRefundsAvailable },
          });
        } else {
          toast('환불에 실패했습니다.', {
            pauseOnFocusLoss: false,
            bodyStyle: {
              textAlign: 'center',
            },
          });
        }
        close();
      },
    });
  };

  const submitKakaoOrderRefund = () => {
    if (kakaoOrderRefundReason && data) {
      const requestBody: RequestCancelReservation = {
        cancellationFeeCalculateType: CancellationFeeCalculateType.AMOUNT,
        cancellationFee: 0,
        personInCharge: {
          type: REFUND_REASON_FAULTER[kakaoOrderRefundReason],
          reasonType: kakaoOrderRefundReason,
          reason: memoRef.current?.value || '',
        },
        cancelRequestTargetUnits: refundTargets.map((refundTarget) => ({
          bookingPriceUnitId: refundTarget.uid!,
          status: refundTarget.status as KakaoReservationStatus,
        })),
        totalRefundAmount: membershipRefundAmount,
        isCancelledByAuth: true,
      };

      changeKakaoReservationStatusMutation.mutate({ type: 'cancel', bookingId: data.uid, body: requestBody });
    }
  };

  useEffect(() => {
    setRefundTargets([]);
    setKakaoOrderRefundReason(undefined);
    if (memoRef?.current) {
      memoRef.current.value = '';
    }
  }, [show]);

  useEffect(() => {
    if (!show || data?.channel !== SalesChannelsType.EZWEL) {
      return;
    }
    if (data?.statuses?.refund === RefundStatus.ALL) {
      return;
    }
    close();
    dialog.open({
      title: '이지웰 환불 안내',
      content: (
        <div>
          이지웰 결제건은 어드민에서 환불할 수 없습니다.
          <br />
          상품주문번호와 상세내용을 이지웰 소통 담당자에게 전달해주세요.
        </div>
      ),
      type: 'ok',
    });
  }, [show, data]);

  useEffect(() => {
    setNoMoreRefundsAvailable((data?.refund?.data?.[0] as RefundLog)?.noMoreRefundsAvailable || false);
  }, [data, show]);

  useEffect(() => {
    // const acceptedStatus = [RefundStatus.ACCEPTED, RefundStatus.RESERVED];
    // const isAccepted = acceptedStatus.includes(data?.statuses.refund || RefundStatus.NONE);
    // const acceptedAmount = isAccepted && data?.refund?.acceptedAmount ? data?.refund?.acceptedAmount : 0;

    const originalRefundAmount = refundTargets.reduce((prev, cur) => prev + (cur.fee || 0), 0);
    setRefundAmount(originalRefundAmount || 0);
  }, [refundTargets]);

  useEffect(() => {
    if (data?.channel) {
      setRefundTargets((prev) => {
        let newValue = [...prev];
        const purchases = data?.purchases?.length > 0 ? data.purchases : data?.purchase ? [data.purchase] : [];
        const newPurchases = purchases.map((item) => {
          return { ...item, fee: item.count * (item.fee || 0) };
        });
        newValue = newValue.concat(newPurchases);
        return newValue;
      });
    } else {
      if (data?.statuses.refund && refundStatusesByUser.includes(data.statuses.refund)) {
        (data.refund.refundItems || data.refund.purchases)?.forEach((item) =>
          setRefundTargets((prev) => {
            const newValue = [...prev];
            if (
              data?.channel === SalesChannelsType.NAVER_STORE &&
              ![RefundStatus.ALL, RefundStatus.PARTIAL].includes(data?.statuses?.refund)
            ) {
              item.fee = data?.payment?.amount || 0;
            }
            newValue.push({ ...item, fee: (item.count || 1) * (item.fee || 0) });

            return newValue;
          }),
        );
      }
    }
  }, [data, show]);

  const handleKakaoOrderChangeStatus = (status: KakaoReservationStatus) => {
    const convertStatus = {
      [KakaoReservationStatus.COMPLETED]: KakaoReservationStatus.CONFIRMED,
      [KakaoReservationStatus.CONFIRMED]: KakaoReservationStatus.COMPLETED,
      [KakaoReservationStatus.CANCELLED]: undefined,
      [KakaoReservationStatus.REQUESTED]: undefined,
    };

    dialog.open({
      type: 'ok_cancel',
      title: '상태 변경',
      content:
        status === KakaoReservationStatus.COMPLETED
          ? '상태값을 이용완료로 변경하시겠습니까?'
          : '상태값 이용 완료를 철회하시겠습니까?',
      onConfirm: async () => {
        const changeStatusTarget =
          data?.purchases.filter((refundTarget) => refundTarget.status === convertStatus[status]) || [];

        if (changeStatusTarget.length === 0 || !data) {
          toast.dark('상태값 변경이 가능한 항목이 없습니다.', {
            pauseOnFocusLoss: false,
            bodyStyle: {
              textAlign: 'center',
            },
          });
          return;
        }

        changeKakaoReservationStatusMutation.mutate({
          type: status === KakaoReservationStatus.COMPLETED ? 'complete' : 'retract-complete',
          bookingId: data.uid,
          body: {
            bookingItemPriceUnitIds: changeStatusTarget.map((target) => target.uid as string),
          },
        });
      },
    });
  };

  return (
    <Modal
      centered
      open={show}
      closable={false}
      footer={null}
      width={1000}
      zIndex={998}
      destroyOnClose
      onCancel={() => {
        close();
      }}>
      <section className="refund-mode" style={{ position: 'relative' }}>
        <SlideMenu
          className="refund-mode__container"
          label={
            <h2 className="refund-mode__title">{`결제 정보 (${getChannelText(
              data?.channel as SalesChannelsType,
            )} 결제)`}</h2>
          }
          defaultOn>
          {data?.channel === SalesChannelsType.KAKAO_RESERVATION && (
            <div style={{ position: 'absolute', top: 0, left: '200px' }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <Button onClick={() => handleKakaoOrderChangeStatus(KakaoReservationStatus.COMPLETED)}>이용완료</Button>
                <Button onClick={() => handleKakaoOrderChangeStatus(KakaoReservationStatus.CONFIRMED)}>
                  이용완료 철회
                </Button>
              </div>
            </div>
          )}
          <section className="refund-mode__memo">
            <section className="refund-mode__memo-item" key={PURCHASE_INFO.title}>
              <h3 className="refund-mode__sub-title">{PURCHASE_INFO.title}</h3>
              <article>
                {PURCHASE_INFO.childrens.map((children) => (
                  <div className="refund-mode__text-container" key={children.key} style={{ whiteSpace: 'pre-wrap' }}>
                    <p className="refund-mode__label">{children.key}</p>
                    <p>{children.value}</p>
                  </div>
                ))}
                <div className="refund-mode__text-container">
                  <p className="refund-mode__label">결제상품</p>
                  <p>{data?.product.name || '-'}</p>
                </div>
                <div className="refund-mode__text-container--column">
                  <p className="refund-mode__label">결제 옵션</p>
                  {data?.purchases?.map((info) => (
                    <p key={JSON.stringify(info.name)}>
                      &nbsp;&nbsp;- {info.name} ({info.fee?.toLocaleString()}원*{info.count}개)
                    </p>
                  ))}
                </div>
              </article>
            </section>
            {remainAmount > 0 && (
              <section
                style={{
                  backgroundColor: '#c5e0ff',
                  borderRadius: '8px',
                  padding: '10px',
                  marginTop: '10px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  textAlign: 'right',
                }}>
                잔여금액: {remainAmountText} {remainCouponDiscountAmountText}
              </section>
            )}
          </section>
        </SlideMenu>
        {data?.shipping && (
          <SlideMenu
            className="refund-mode__container"
            label={<h2 className="refund-mode__title">배송정보</h2>}
            defaultOn>
            <section className="refund-mode__memo">
              <section className="refund-mode__memo-item-container">
                <article>
                  {Object.keys(data?.shipping).map((key) => {
                    const label = SHIPPING_LABELS[key];
                    return (
                      <div className="refund-mode__text-container" key={key}>
                        <p className="refund-mode__label">{label}</p>
                        <p className="refund-mode__text">{(data?.shipping as { [key: string]: string })[key]}</p>
                      </div>
                    );
                  })}
                  <div className="refund-mode__text-container">
                    <p className="refund-mode__label">배송메모</p>
                    <p className="refund-mode__text">{data?.memo?.shipping}</p>
                  </div>
                </article>
              </section>
            </section>
          </SlideMenu>
        )}
        <SlideMenu className="refund-mode__container" label={<h2 className="refund-mode__title">메모</h2>} defaultOn>
          <section className="refund-mode__memo">
            <section className="refund-mode__memo-item-container">
              {REFUND_MEMO.map((item) => (
                <section className="refund-mode__memo-item" key={item.title}>
                  <h3 className="refund-mode__sub-title">{item.title}</h3>
                  <article>
                    {item.childrens.map((children) => (
                      <div className="refund-mode__text-container" key={children.key}>
                        <p className="refund-mode__label">{children.key}</p>
                        <p className="refund-mode__text">{children.value}</p>
                      </div>
                    ))}
                  </article>
                </section>
              ))}
            </section>
            {data?.product.saleType === 'commerce' && (
              <section className="refund-mode__memo-item refund-mode__cost">
                <p className="refund-mode__cost-item">
                  요청 환불 금액:{' '}
                  {data?.statuses.refund === RefundStatus.REQUESTED || data?.statuses.refund === RefundStatus.ACCEPTED
                    ? data?.refund?.requestedAmount?.toLocaleString() || 0
                    : 0}{' '}
                  원
                </p>
                <p className="refund-mode__cost-item">
                  승인 환불 금액:{' '}
                  {data?.statuses.refund === RefundStatus.ACCEPTED ? data?.refund.acceptedAmount?.toLocaleString() : 0}
                  원
                </p>
              </section>
            )}
          </section>
        </SlideMenu>
        {refundDoneHistory.length > 0 ? (
          <SlideMenu className="refund-mode__container" label={<h2 className="refund-mode__title">이전 환불 내역</h2>}>
            <section className="refund-mode__memo">
              {refundDoneHistory.map((item, idx) => (
                <section className="refund-mode__memo-item" key={idx}>
                  <h3 className="refund-mode__sub-title">환불내역-{idx + 1}</h3>
                  <article>
                    <div className="refund-mode__text-container">
                      <p className="refund-mode__label">환불 금액</p>
                      <p>
                        {item.amount?.toLocaleString()}원({item.point?.toLocaleString()}P)
                      </p>
                    </div>
                    <div className="refund-mode__text-container">
                      <p className="refund-mode__label">환불 시각</p>
                      <p>{moment(item.createdAt).format('YYYY-MM-DD HH:mm')}</p>
                    </div>
                    <div className="refund-mode__text-container">
                      <p className="refund-mode__label">환불 메모</p>
                      <p>{'memo' in item ? item.memo : '-'}</p>
                    </div>
                    <div className="refund-mode__text-container--column">
                      <p className="refund-mode__label">환불 상품</p>
                      {(item?.refundItems || []).map((info) => (
                        <p key={JSON.stringify(info.name)}>
                          &nbsp;&nbsp;- {info.name} ({info.fee?.toLocaleString()}원*{info.count}개)
                        </p>
                      ))}
                    </div>
                    {item.receiptUrl && (
                      <div className="refund-mode__text-container">
                        <p
                          className="refund-mode__receipt"
                          onClick={() => {
                            window.open(`${item.receiptUrl}`, '_blank', 'width=600, height=400, top= 100, left=-50');
                          }}>
                          [환불 영수증]
                        </p>
                      </div>
                    )}
                  </article>
                </section>
              ))}
            </section>
          </SlideMenu>
        ) : null}
        {data?.product.saleType === 'commerce' && reservedItems.length > 0 && (
          <section>
            <section className="refund-mode__container--row">
              <h2 className="refund-mode__title">환불 예약 내역</h2>
              <button className="refund-mode__btn refund-mode__btn--small" onClick={cancelReserveRefundPayment}>
                환불예약 취소
              </button>
            </section>
            <Table columns={REFUND_RESERVE_COLUMNS} dataSource={reservedItems} pagination={false} />
          </section>
        )}
        {remainAmount > 0 && (
          <section className="refund-mode__container">
            <div className="refund-mode__container--row">
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <h2 className="refund-mode__title">결제 내역(환불 대상)</h2>
                {(data?.channel === SalesChannelsType.NAVER_STORE ||
                  data?.channel === SalesChannelsType.KAKAO_RESERVATION) && (
                  <div style={{ color: 'red', fontWeight: 'bold' }}>
                    - 네이버/카카오 결제내역은 전체환불만 가능합니다.
                  </div>
                )}
              </div>
              {data?.channel === SalesChannelsType.KAKAO_RESERVATION && (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                  <div style={{ fontWeight: 'bold' }}>(필수)카카오 결제 환불사유</div>
                  <Select
                    value={kakaoOrderRefundReason}
                    placeholder="(필수) 환불 사유 선택"
                    popupMatchSelectWidth={false}
                    options={[
                      {
                        value: CancelReasonType.PURCHASER_REQUEST,
                        label: '구매자 요청',
                      },
                      {
                        value: CancelReasonType.RE_BOOKING,
                        label: '재예약 진행',
                      },
                      {
                        value: CancelReasonType.HEALTH_REASON,
                        label: '건강상의 이유',
                      },
                      {
                        value: CancelReasonType.THIRD_PARTY_LOWEST_PRICE,
                        label: '타사 최저가 이용',
                      },
                      {
                        value: CancelReasonType.SIMPLE_REMORSE,
                        label: '단순 변심',
                      },
                      {
                        value: CancelReasonType.STORE_SUSPENSION,
                        label: '장소 운휴',
                      },
                      {
                        value: CancelReasonType.BOOKING_CLOSED,
                        label: '예약 마감',
                      },
                      {
                        value: CancelReasonType.SHORTAGE_OF_PURCHASERS,
                        label: '모객 부족',
                      },
                      {
                        value: CancelReasonType.NATURAL_DISASTER,
                        label: '천재지변',
                      },
                      {
                        value: CancelReasonType.INFECTIOUS_DISEASE_SAFETY_RULES,
                        label: '감염병 안전수칙 준수',
                      },
                    ]}
                    onChange={(value) => setKakaoOrderRefundReason(value)}
                    style={{ width: '170px' }}
                  />
                </div>
              )}
              {reservedItems.length < 1 && data?.product.saleType === 'commerce' && (
                <section className="refund-mode__option">
                  <Checkbox disabled checked={data?.refund?.noMoreRefundsAvailable || false}>
                    현재 "추가 환불 불가" 상태
                  </Checkbox>
                  <Checkbox
                    checked={noMoreRefundsAvailable}
                    onChange={() => setNoMoreRefundsAvailable(!noMoreRefundsAvailable)}>
                    추가 환불 불가
                  </Checkbox>
                  <button
                    className="refund-mode__btn refund-mode__btn--small"
                    onClick={reserveRefundPayment}
                    disabled={refundTargets.length === 0 || membershipRefundAmount > remainAmount}>
                    환불예약
                  </button>
                </section>
              )}
            </div>
            <Table
              columns={REFUND_COLUMNS}
              dataSource={(data?.purchases || []).filter((purchase) => purchase.name !== 'discount')}
              pagination={false}
            />
          </section>
        )}
        {remainAmount > 0 && (
          <section className="refund-mode__container--row" style={{ gap: '12px' }}>
            <section style={{ flex: 1 }}>
              <h4 className="refund-mode__label">환불메모</h4>
              <textarea
                className="refund-mode__textarea"
                ref={memoRef}
                rows={2}
                placeholder="(선택) 환불 메모를 입력해 주세요!"
              />
            </section>
            <section className="refund-mode__total-cost">
              <h4 className="refund-mode__label">총 환불 금액</h4>
              <InputNumber
                disabled
                size="large"
                value={refundAmount || 0}
                min={0}
                max={originalRemainAmount}
                onChange={(value) => {
                  if (value !== undefined && value !== null) {
                    if (value <= remainAmount) {
                      setRefundAmount(value);
                    }
                  }
                }}
              />
            </section>
            {data?.buyer.membership && (
              <section className="refund-mode__total-cost">
                <h4 className="refund-mode__label">멤버십 반영 환불금액</h4>
                <InputNumber disabled size="large" value={membershipRefundAmount} min={0} max={originalRemainAmount} />
              </section>
            )}
            <button
              className="refund-mode__btn refund-mode__btn--big"
              disabled={membershipRefundAmount > remainAmount}
              onClick={() => submitRefund()}>
              환불처리
            </button>
          </section>
        )}
      </section>
    </Modal>
  );
};

export default RefundModeModal;
